<script lang="ts">
  import { onMount } from 'svelte';
  import { _ } from 'svelte-i18n';
  import Close from '../../icons/Close.svelte';
  import {
    AppState,
    SetIconsBackground,
    SetNotification,
    ToastQueue
  } from '../../../etc/stores';
  import MessagePopup from '../../misc/MessagePopup.svelte';
  import ThemedIcon from '../../misc/ThemedIcon.svelte';
  import { Container } from '../../../services/container';
  import { Auth } from '../../../services/auth';
  import { Logger } from '../../../services/logger';
  import PoweredBy from '../../misc/PoweredBy.svelte';
  import { replaceVariables } from '../../../i18n';
  import AuthConnector from '../../misc/AuthConnector.svelte';
  import Heading from '../../typography/Heading.svelte';
  import Text from '../../typography/Text.svelte';
  import ArrowRight from '../../misc/ArrowRight.svelte';
  import RedemptionAction from '../../misc/RedemptionAction.svelte';
  import {
    formatMoney,
    getErrorMessage,
    isSafariBrowser,
    removeTrailingZeros
  } from '../../../etc/utils';
  import type { ICoupon } from '../../../etc/types';
  import { FreeCoinsCampaign } from '../../../services/free-coins-campaign';
  import { extractMessageFromError } from '../../../services/api';
  import { Backend } from '../../../services/backend';
  import { Customer } from '../../../services/customer';
  import {
    AUTH_STORAGE_FLAGS,
    SessionManager
  } from '../../../services/session-manager';
  import { ReferralProgram } from '../../../services/referral-program';
  import Analytics from '../../../services/analytics';
  import { fly } from 'svelte/transition';
  import sessionKeys from '../../../etc/sessionKeys';

  interface IBestIniciative {
    value: number;
    iniciative: 'welcome-coins' | 'convert' | 'coupon' | 'referral';
    coupon?: ICoupon;
  }

  let setLoading;
  let handleConnectCustomer;
  let loadingData = true;

  let redemptionData: any = {};
  let value = '';
  let category = `fast-redemption-${Analytics.getCategoryLoginState()}`;
  let onCopy;

  const logger = Container.get(Logger).context(
    'FastRedemption.svelte'
  ) as Logger;
  const auth = Container.get(Auth);

  const freeCoinsCampaign = Container.get(
    FreeCoinsCampaign
  ) as FreeCoinsCampaign;

  const referralProgram = Container.get(ReferralProgram) as ReferralProgram;

  const backend = Container.get(Backend) as Backend;
  const customer = Container.get(Customer);
  const session = Container.get(SessionManager);

  const t = (text: string, values: any = {}) => {
    return replaceVariables($_(`fast-redemption.${text}`), values);
  };

  const getCouponValue = (coupon: any) => {
    const value = (coupon.moneyValue || 0) / 100;

    if (coupon.initiative === 'referral-program')
      return (value / 100) * (session.get(sessionKeys.CHECKOUT_VALUE) || 0);
    return value;
  };

  const animate = node => {
    return fly(node, { y: 50 });
  };

  const updateRedemption = () => {
    const walletCoins = Number(($AppState.customer.wallet || {}).coins) || 0;

    const checkoutValue = session.get(sessionKeys.CHECKOUT_VALUE) || 0;
    const convertCoins =
      walletCoins > checkoutValue ? checkoutValue : walletCoins;

    const possibitiesIniciatives = [
      { iniciative: 'convert', value: convertCoins },
      {
        iniciative: 'welcome-coins',
        value: Number($AppState.freeCoinsCampaign.coinsReward) || 0
      },
      ...($AppState.coupons
        ? $AppState.coupons
            .filter(item => item.status === 'active')
            .map(item => ({
              iniciative: 'coupon',
              value: getCouponValue(item),
              coupon: item
            }))
        : [])
    ];

    if (
      checkoutValue &&
      $AppState.referralReferredState.businessReferral?.isActive
    ) {
      const percentageReferral =
        Number(
          $AppState.referralReferredState.businessReferral.referredDiscount
        ) / 100;
      possibitiesIniciatives.unshift({
        iniciative: 'referral',
        value: percentageReferral * checkoutValue || 0
      });
    }

    let bestIniciativeValue: IBestIniciative;
    if (checkoutValue) {
      bestIniciativeValue = possibitiesIniciatives
        .filter(item => item.value <= checkoutValue)
        .reduce(function (prev, current) {
          return prev.value > current.value ? prev : current;
        }) as IBestIniciative;
    }

    if (!bestIniciativeValue || bestIniciativeValue.value === 0) {
      SetNotification('');
      return;
    }

    if (!value)
      Analytics.logEvent(`render-${bestIniciativeValue.iniciative}`, category);

    switch (bestIniciativeValue.iniciative) {
      case 'welcome-coins':
        redemptionData = {
          ...$AppState.freeCoinsCampaign,
          redeemType: bestIniciativeValue.iniciative
        };
        value = formatMoney(
          $AppState.business.location,
          $AppState.freeCoinsCampaign.coinsReward || 0,
          false
        );
        break;

      case 'coupon':
        if (bestIniciativeValue.coupon) {
          value = removeTrailingZeros(
            bestIniciativeValue.coupon.formattedMoneyAmount
          );
          redemptionData = {
            ...bestIniciativeValue.coupon,
            redeemType: bestIniciativeValue.iniciative
          };
        }
        break;

      case 'convert':
        value = formatMoney(
          $AppState.business.location,
          bestIniciativeValue.value || 0,
          false
        );
        redemptionData = {
          redeemType: bestIniciativeValue.iniciative,
          value,
          coins: Number.isInteger(bestIniciativeValue.value)
            ? bestIniciativeValue.value
            : bestIniciativeValue.value.toFixed(2)
        };

        break;
      case 'referral':
        value = `${Number(
          $AppState.referralReferredState.businessReferral.referredDiscount
        )}%`;
        redemptionData = {
          redeemType: bestIniciativeValue.iniciative,
          value,
          name: $AppState.referralReferredState.referringCustomer.name
        };
        break;

      default:
        break;
    }
    loadingData = false;
  };

  const closeMessage = () => {
    SetNotification('');
    Analytics.logEvent(`click-close`, category);

    AppState.update(state => ({
      ...state,
      website: {
        ...state.website,
        connectingAccount: false,
        authFlow: 'default'
      }
    }));
  };

  const handleCopy = () => {
    if (!isSafariBrowser())
      setTimeout(() => {
        if (onCopy) onCopy(false);
      });
  };

  const redeemCoupon = async () => {
    if (setLoading) setLoading(true);

    try {
      if (redemptionData.redeemType === 'referral') {
        Analytics.logEvent(`claim-referral`, category);
        try {
          await referralProgram.claimReferredCoupon({
            fastRedemption: true
          });
          session.remove(referralProgram.REFERRAL_REFERRED_STATE);
          AppState.update(state => ({
            ...state,
            referralReferredState: {}
          }));
        } catch (err) {
          if (
            err.response.status === 400 &&
            err.response.data.slug ===
              'exceptions.businessReferralCampaign.alreadyParticipated'
          ) {
            SetNotification('referredAlreadyJoined', true);
            return;
          } else if (
            $AppState.customer.id ===
            $AppState.referralReferredState.referringCustomer.id
          ) {
            SetNotification('referYourselfError', true);
            return;
          } else {
            logger.error(err);
            throw new Error('Unexpected error');
          }
        }
      } else {
        await freeCoinsCampaign.claimCampaignCoupon({
          fastRedemption: true,
          analyticsCategory: category
        });
      }
      const response = await backend.getCoupons($AppState.business.id);
      if (
        response.coupons &&
        response.coupons.filter(coupon => coupon.status === 'active').length > 0
      ) {
        AppState.update(state => ({
          ...state,
          coupons: response.coupons
        }));
      }
      updateRedemption();

      handleCopy();
    } catch (err) {
      ToastQueue.add({
        title: $_('error.title'),
        message: extractMessageFromError(err),
        type: 'error',
        duration: 5000
      });
      if (err.status != 400) {
        logger.error(err);
      }
    }
    setLoading(false);
  };

  const handleConvert = async () => {
    setLoading(true);
    const amountInCents = redemptionData.coins * 100;
    try {
      Analytics.logEvent(`click-convert-confirm`, category);
      const response = await backend.createCoupon({
        oauthId: $AppState.business.oauthId,
        value: Number(amountInCents.toFixed(2)),
        fastRedemption: true
      });

      const coupons = [...$AppState.coupons, response];

      AppState.update(state => ({
        ...state,
        coupons
      }));

      await customer.loadState();
      updateRedemption();

      handleCopy();
    } catch (e) {
      logger.error(e);
      const message = getErrorMessage(e);

      ToastQueue.add({
        title: $_('error.title'),
        message,
        type: 'error',
        duration: 5000
      });
    }
    setLoading(false);
  };

  const onAfterConnectCustomer = async next => {
    if (next.route === '/dashboard') {
      await redeemCoupon();
    }
  };

  const handleSubmit = async () => {
    AppState.update(state => ({
      ...state,
      website: {
        ...state.website,
        authFlow: 'fast-redemption'
      }
    }));
    session.set(AUTH_STORAGE_FLAGS.fastRedemption, true);

    if ($AppState.auth.connected) {
      redeemCoupon();
    } else if (await auth.isAuthenticatedOnWebsite()) {
      handleConnectCustomer();
    } else if (auth.canOpenLoginModal()) {
      auth.openLoginModal();
    }
  };

  const triggerConnectCustomer = () => {
    SetIconsBackground('light-without-background');

    if ($AppState.auth.connected) {
      redeemCoupon();
    } else {
      handleConnectCustomer();
    }
  };

  const goToMyWallet = async () => {
    Analytics.logEvent(`click-go-to-wallet`, category);

    AppState.update(state => ({
      ...state,
      website: {
        ...state.website,
        category: '',
        popoverOpen: true,
        popoverRouteOnOpen: {
          route: '/dashboard'
        }
      }
    }));
  };

  onMount(() => {
    updateRedemption();
  });
</script>

{#if !loadingData}
  <AuthConnector
    {triggerConnectCustomer}
    {onAfterConnectCustomer}
    loggerContext="FastRedemption.svelte"
    bind:handleConnectCustomer
    bind:setLoading
    let:loading
  >
    <div class="fast-redemptions" transition:animate>
      <MessagePopup>
        <div class={`fast-redemptions__wrapper --${auth.provider}`}>
          <div class="fast-redemptions__header">
            <img
              class="fast-redemptions__logo"
              src={$AppState.business.logo.publicPath.small}
              alt="Business Logo"
            />
            <Heading type="h2" color="#FFF">Use now!</Heading>
            <div class="fast-redemptions__close" on:click={closeMessage}>
              <ThemedIcon theme="none">
                <Close />
              </ThemedIcon>
            </div>
          </div>
          <div class="fast-redemptions__content">
            <RedemptionAction
              {redemptionData}
              {value}
              {loading}
              on:redeem={handleSubmit}
              on:convert={handleConvert}
              bind:onCopy
            />
          </div>
          <div class="fast-redemptions__footer">
            {#if $AppState.auth.connected}
              <div class="fast-redemptions__button" on:click={goToMyWallet}>
                <Text type="default" bold color="#FFF">{t('btn-wallet')}</Text>
                <ArrowRight height={12} width={12} color="#FFF" />
              </div>
            {/if}

            <PoweredBy color={$AppState.theme.colors.primary400} />
          </div>
        </div>
      </MessagePopup>
    </div>
  </AuthConnector>
{/if}

<style lang="scss">
  @use './styles.scss' as *;

  .fast-redemptions {
    &__wrapper {
      height: 100%;
      width: 100%;
      background-color: var(--twism-theme-background);
      padding: 24px 32px;
      box-sizing: border-box;
      &.--wix {
        @media screen and (max-width: 468px) {
          padding: 18px;
        }
      }
    }

    :global(.twism-popup--container) {
      padding: 0 !important;
    }

    &__header {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 24px;

      :global(.twism-themed-icon) {
        background: rgba(255, 255, 255, 0.2) !important;
        border-radius: 8px;
      }
    }

    &__logo {
      width: 32px;
      height: 32px;
      box-sizing: border-box;
      border-radius: 50%;
    }

    &__content {
      margin-bottom: 32px;
    }

    &__footer {
      display: flex;
      flex-direction: column;
      width: fit-content;
      margin-inline: auto;
    }

    &__button {
      display: flex;
      margin-bottom: 32px;
      gap: 12px;
      cursor: pointer;
    }

    &__close {
      cursor: pointer;
    }
  }
</style>
